import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faClock,
  faBox,
  faTruck,
  faCheckCircle,
  faExclamationCircle,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import type { OrderTabType } from './types';

export const ORDER_TABS: OrderTabType[] = [
  'pending',
  'preparing',
  'ready',
  'completed',
  'history',
];

export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'ready', label: 'Ready' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

export const TAB_LABELS: Record<OrderTabType, string> = {
  pending: 'Pending',
  preparing: 'Preparing',
  ready: 'Ready',
  completed: 'Completed',
  history: 'History',
};

export const TAB_ICONS: Record<OrderTabType, IconDefinition> = {
  pending: faClock,
  preparing: faBox,
  ready: faTruck,
  completed: faCheckCircle,
  history: faChartLine,
};

export const MESSAGES = {
  FETCH_ERROR: 'Failed to fetch orders. Please try again.',
  ERROR_TITLE: 'Error',
} as const;

export interface FormattedDateTime {
  formattedDate: string;
  formattedTime: string;
}

export const addCommasInPrices = (value: number | string): string => {
  return (parseFloat(String(value)) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const STATUS_COLORS: Record<string, string> = {
  pending: 'gold',
  preparing: 'blue',
  ready: 'orange',
  completed: 'green',
  cancelled: 'red',
};

export const getStatusColor = (status: string | undefined): string => {
  const key = status?.toLowerCase();
  return (key && STATUS_COLORS[key]) || 'default';
};

export const STATUS_ICONS: Record<string, IconDefinition> = {
  pending: faClock,
  preparing: faBox,
  ready: faTruck,
  completed: faCheckCircle,
  cancelled: faExclamationCircle,
};

export const getStatusIcon = (status: string | undefined): IconDefinition => {
  const key = status?.toLowerCase();
  return (key && STATUS_ICONS[key]) || faClock;
};

export const getStatusLabel = (status: string | undefined): string => {
  const key = status?.toLowerCase();
  return (key && TAB_LABELS[key as OrderTabType]) || 'Unknown';
};

export const formatDateAndTime = (isoString: string | undefined): FormattedDateTime => {
  if (!isoString) return { formattedDate: '—', formattedTime: '—' };
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString('en-GB');
  const formattedTime = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return { formattedDate, formattedTime };
};

export const getPhoneBlurred = (phone: string | undefined): string => {
  if (!phone) return '';
  return phone.length > 4 ? phone.slice(0, -4) : phone.slice(0, -2);
};

export const getPhoneUnBlurred = (phone: string | undefined): string => {
  if (!phone) return '';
  return phone.length > 4 ? phone.slice(-4) : phone.slice(-2);
};

const getMerchantTax = (): number => {
  try {
    const user = localStorage.getItem('user');
    if (!user) return 0;
    const parsed = JSON.parse(user);
    return parseFloat(parsed?.tax) || 0;
  } catch {
    return 0;
  }
};

export const computeOrderSubtotal = (order: {
  total_price?: string;
  tip?: string;
}): number => {
  const total = parseFloat(order.total_price || '0') || 0;
  const tip = parseFloat(order.tip || '0') || 0;
  const taxRate = getMerchantTax();
  const taxAmount = total * taxRate;
  return total - tip - taxAmount;
};

export const computeOrderTax = (order: { total_price?: string }): number => {
  const total = parseFloat(order.total_price || '0') || 0;
  return total * getMerchantTax();
};
